import './ninja-cut.css';
import { url as basePathUrl } from '../../js/base-path';

// The canvas is always opaque — the engine paints a cover-fit background image onto it every
// frame (falling back to a linear gradient from colors.bgStart/bgEnd). The play area gets its
// own level art (the milk valley) rather than the modal frame's background: the full-bleed
// canvas covers the frame and its decor layers while playing anyway.
import levelBackground from '../../images/game/level-bg.webp';

// The engine ships as a global (window.NinjaCutGame) loaded via a <script> tag — not as an
// ES module — mirroring how peroni-f1-2026 integrates sepho-race and how the standalone
// build (../sephora-games/standalone/ninja-cut) wires it.
// Root-relative on purpose: basePathUrl() dokleja prefiks instalacji (np. /konkurs) w chwili
// startu gry, a nie przy ładowaniu modułu — `document.body` musi już istnieć.
const SCRIPT_PATH = '/build/games/ninja-cut/ninja-cut.min.js';

// ── Image assets ──────────────────────────────────────────────────────────────
// Sprites live with the rest of the campaign art in assets/images/game and are imported
// through webpack so they get hashed/published. The engine accepts a plain URL per entry;
// none of them carries a splash set, so a sliced object splits into two halves instead of
// bursting into debris (the engine's _spawnDebris fallback).
import appleSprite from '../../images/game/apple.webp';
import blueberrySprite from '../../images/game/blueberry.webp';
import peachSprite from '../../images/game/peach.webp';
import strawberrySprite from '../../images/game/strawberry.webp';
import chocolateSprite from '../../images/game/chocolate.webp';
import badSprite from '../../images/game/bad.webp';

// Fruit is "good", chocolate is the "bonus" (worth pointsPerBonus), bad.webp costs a life.
// An entry is either a plain URL or {img, radius}: the engine reads a positive `radius` per
// sprite and falls back to gameConfig.objectRadius, so `bad` is deliberately the bigger object.
// The radius drives the drawn size AND the hitbox (see the engine's _spawnObject / _sliceAlong).
const GOOD_SPRITES = [appleSprite, blueberrySprite, peachSprite, strawberrySprite];
const BONUS_SPRITES = [chocolateSprite];
const BAD_SPRITES = [{img: badSprite, radius: 58}];

const spriteUrl = (entry) => (entry && typeof entry === 'object') ? entry.img : entry;

// autoStart: true → the engine runs straight into play; the surrounding modal owns the
// loading / instructions / result / answer screens (see the modal handler).
const GAME_CONFIG = {
    duration: 60,
    lives: 1,
    autoStart: true,

    // The engine sizes the canvas to min(container width, maxPlayWidth) × min(viewport
    // height, maxPlayHeight). The modal frame is full-viewport in both orientations, so keep
    // these above any realistic viewport: the play area then fills the frame edge to edge
    // instead of leaving a portrait strip on desktop.
    maxPlayWidth: 2560,
    maxPlayHeight: 1920,

    gravity: 1300,
    launchSpeedMin: 900,
    launchSpeedMax: 1250,
    lateralSpeedMax: 260,

    spawnInterval: 2,
    spawnJitter: 0.5,
    maxPerWave: 3,
    badChance: 0.28,
    bonusChance: 0.08, // chocolate (bonus) spawn probability; badChance + bonusChance must stay < 1
    objectRadius: 60,
    spawnMargin: 0.12,
    difficultyRamp: {
        spawnIntervalScale: 0.1,
        speedScale: 3,
    },

    pointsPerGood: 10,
    pointsPerBonus: 50,
    comboWindow: 0.35,
    comboBonus: 2,
    maxCombo: 5,

    trailMaxPoints: 25,
    trailMaxAge: 0.3,
    bladeWidth: 16,

    halfLifetime: 0.9,
    halfSeparation: 140,
    halfSpin: 6,

    popupLifetime: 0.8,

    heartbeatInterval: 10000,
};

// Zawartość paska HUD silnika. UWAGA: silnik czyta HUD z `assetsConfig.hud` (patrz `_buildHud`),
// a NIE z `gameConfig` — pozycja wstawiona do konfiguracji gry jest po cichu ignorowana.
//  * gra próbna → jedna etykieta („GRA PRÓBNA"), żeby było widać, że wynik się nie liczy,
//  * gra na punkty → pusto; wynik pokazuje własny .modal-hud modala.
function buildHudConfig(demo) {
    return demo ? [{type: 'text', text: 'GRA PRÓBNA'}] : [];
}

// Branding / assets config — texts & colors from config.assets.js, sprites imported above.
// Demo and real runs share it; only the HUD, gameConfig and apiConfig differ (see startNinjaCut).
function buildAssetsConfig(demo = false) {
    return {
        theme: 'lech',
        texts: {
            gameTitle: 'NinjaCut',
            gameSubtitle: 'Tnij dobre, omijaj złe!',
            startButton: 'Graj',
            loading: 'Ładowanie...',
            hudScore: 'Wynik',
            hudTime: 'Czas',
            hudLives: 'Życia',
            comboToast: 'Combo x{combo}!',
            resultTitle: 'Koniec gry',
            resultTimeUp: 'Czas minął!',
            resultFail: 'Brak żyć!',
            resultScore: 'Twój wynik',
            playAgain: 'Zagraj ponownie',
        },
        hud: buildHudConfig(demo),
        images: {
            background: levelBackground,
            good: GOOD_SPRITES,
            bad: BAD_SPRITES,
            bonus: BONUS_SPRITES,
            // With the HUD gone the engine never renders hearts, so it needs no heart sprites.
            heartFull: null,
            heartEmpty: null,
        },
        sounds: {
            slice: null,
            bomb: null,
        },
        tokens: {
            fontFamily: "'Montserrat', 'Open Sans', 'Arial Black', sans-serif",
        },
        colors: {
            // Only used if `images.background` fails to load — keep it on the frame's fallback blue.
            bgStart: '#0063a5',
            bgEnd: '#0063a5',
            blade: '#fffefd',
            bladeGlow: '#f6ede0',
            good: '#FF66CC',
            bad: '#212121',
            scorePopup: '#FFF9C4',
            primary: 'white',
        },
    };
}

function apiBase(uuid) {
    return basePathUrl(`/api/application/${uuid}/ninja-cut`);
}

/**
 * Report an out-of-band gameplay event (pauza / wznowienie) to the GameLog.
 *
 * Silnik nie wysyła nic poza /start, /heartbeat i /result, a pauza akurat WSTRZYMUJE heartbeaty
 * — bez tego wpisu w logu przerwa jest niewidoczna i wygląda jak luka w sekwencji. Stąd osobny
 * /event: sam raportuje, nic nie waliduje po stronie klienta.
 *
 * `keepalive` jest istotne: najczęstszą przyczyną pauzy jest ukrycie karty (visibilitychange),
 * a wtedy przeglądarka potrafi ubić zwykły fetch w trakcie — z keepalive żądanie dojdzie.
 */
function reportGameEvent(uuid, type, payload = {}) {
    const sessionToken = gameInstance?.sessionToken;

    // Gra próbna nie ma sesji (mock API) — nie ma czego logować.
    if (!uuid || !sessionToken) {
        return;
    }

    fetch(`${apiBase(uuid)}/event`, {
        method: 'POST',
        headers: {'Content-Type': 'application/json'},
        body: JSON.stringify({sessionToken, type, ...payload}),
        keepalive: true,
    }).catch(() => { /* telemetria — cicha porażka nie może psuć rozgrywki */ });
}

/**
 * Snapshot rozgrywki dołączany do eventu pauzy/wznowienia — te same pola co w heartbeacie, żeby
 * przegląd logu w adminie czytał się jednolicie. Silnik trzyma je na prywatnych polach i nie
 * udostępnia getterów (tak samo jak `_score`, patrz getNinjaCutScore).
 */
function gameSnapshot() {
    return {
        elapsedMs:  Math.round(gameInstance?._playTimeMs?.() ?? 0),
        score:      gameInstance?._score ?? 0,
        lives:      gameInstance?._lives ?? 0,
        goodCuts:   gameInstance?._goodCuts ?? 0,
        badCuts:    gameInstance?._badCuts ?? 0,
        bonusCuts:  gameInstance?._bonusCuts ?? 0,
        goodMissed: gameInstance?._goodMissed ?? 0,
    };
}

/**
 * Preload every game image so the canvas has them ready before the first frame.
 * `onProgress` is called with a 0..1 fraction as each image settles (load or error),
 * driving the loading screen's 10 life-indicator progress bar. Resolves once everything
 * settled.
 */
export function preloadNinjaCutAssets(onProgress = () => {
}) {
    // The level background counts as an asset here: it is the heaviest single image and the
    // engine draws it on the very first frame, so it must be cached before play starts.
    const urls = [
        levelBackground,
        ...[...GOOD_SPRITES, ...BONUS_SPRITES, ...BAD_SPRITES].map(spriteUrl),
    ];
    const total = urls.length || 1;
    let settled = 0;

    const settle = () => {
        settled += 1;
        onProgress(Math.min(1, settled / total));
    };

    const loadOne = (url) => new Promise((res) => {
        const img = new Image();
        img.onload = () => {
            settle();
            res();
        };
        img.onerror = () => {
            settle();
            res();
        };
        img.src = url;
    });

    return (async () => {
        // Small set — load in parallel; the bar advances as each one settles.
        await Promise.all(urls.map(loadOne));
        onProgress(1);
    })();
}

let gameInstance = null;

function loadScript(src) {
    return new Promise((resolve, reject) => {
        if (window.NinjaCutGame || document.querySelector(`script[src="${src}"]`)) {
            resolve();
            return;
        }
        const s = document.createElement('script');
        s.src = src;
        s.onload = resolve;
        s.onerror = reject;
        document.head.appendChild(s);
    });
}

/**
 * Mount and run the game inside `container`.
 *  - demo === true  → no score registration, no heartbeats (mock API).
 *  - demo === false → registers the score + sends heartbeats to the backend.
 *
 * Ninja Cut posts no per-cut events — the engine's apiClient handles start / heartbeat /
 * result, so (unlike sepho-race) there are no gameplay events to forward.
 * `onDone(score)` is invoked once the game finishes (instance already torn down).
 */
export function startNinjaCut(container, {
    demo = false, uuid = null, onDone = () => {
    }, onError = null
} = {}) {
    teardownNinjaCut();

    const hud = buildHudConfig(demo);

    // Pusty `hud` (gra na punkty) opróżnia bar silnika, ale sam element renderuje się ZAWSZE —
    // a motyw lech rysuje go jako pudełko z ramką. Modyfikator jest hakiem, którym modalowy SCSS
    // usuwa bar w całości; przy niepustym HUD-zie (gra próbna) nie może się pojawić, bo schowałby
    // razem z barem także etykietę.
    container.classList.add('nc-game');
    container.classList.toggle('nc-game--no-hud', hud.length === 0);

    // Trial runs up to 3 min, the real game up to 15 min (the engine counts down from duration).
    // The real-game value must match NinjaCutController::GAME_CONFIG['duration'] — the backend
    // flags a session whose reported play time exceeds it (game_exceeded_duration).
    const gameConfig = {...GAME_CONFIG, duration: demo ? 180 : 900};

    const apiConfig = demo
        // Demo: mock API with no sessionToken → engine heartbeats stay disabled.
        ? {useMock: true, mockDelay: 0, mockResponses: {startGame: {}, submitResult: {}}}
        : {
            useMock: false,
            endpoints: {
                startGame: `${apiBase(uuid)}/start`,
                heartbeat: `${apiBase(uuid)}/heartbeat`,
                submitResult: `${apiBase(uuid)}/result`,
            },
        };

    loadScript(basePathUrl(SCRIPT_PATH))
        .then(() => {
            if (!window.NinjaCutGame) {
                console.error('NinjaCutGame: script loaded but global is not defined');
                onDone(0);
                return;
            }

            gameInstance = window.NinjaCutGame.init(container.id, {
                assetsConfig: buildAssetsConfig(demo),
                gameConfig,
                apiConfig,
                callbacks: {
                    // Silnik mapuje tylko znane nazwy (onComplete/onError/…), a nieznany klucz
                    // wiąże wprost jako nazwę zdarzenia na swoim event busie — dlatego
                    // 'game:pause' / 'game:resume' działają bez zmian w ninja-cut.min.js.
                    // Pauza leci też automatycznie przy ukryciu karty (visibilitychange).
                    'game:pause': () => reportGameEvent(uuid, 'game:pause', gameSnapshot()),
                    'game:resume': () => reportGameEvent(uuid, 'game:resume', gameSnapshot()),
                    onComplete: (result) => {
                        const score = result?.score ?? gameInstance?._score ?? 0;
                        // reason: 'timeup' | 'nolives' | 'forced' — lets the modal tailor the result copy.
                        const reason = result?.reason ?? null;
                        teardownNinjaCut();
                        onDone(score, reason);
                    },
                    onError: (error) => {
                        console.error('NinjaCutGame error:', error);
                        teardownNinjaCut();
                        // A failed /start (e.g. outside play hours, already played) surfaces here as an
                        // ApiError carrying { status, data }. Hand it to the caller so it can show a
                        // pretty screen; fall back to the neutral score-0 result if unhandled.
                        if (onError) {
                            onError(error);
                        } else {
                            onDone(0);
                        }
                    },
                },
            });
        })
        .catch((e) => {
            console.error('NinjaCutGame: failed to load game script', e);
            onDone(0);
        });
}

export function teardownNinjaCut() {
    if (gameInstance) {
        try {
            gameInstance.destroy();
        } catch { /* ignore */
        }
        gameInstance = null;
    }
}

/**
 * Current score of the running game (0 when nothing is running). The engine keeps it on a
 * private field and exposes no getter, so it is read here — in one place — rather than by
 * every caller that wants to mirror the score outside the canvas.
 */
export function getNinjaCutScore() {
    return gameInstance?._score ?? 0;
}

/** Pause a running game (no-op unless the engine is in the "playing" state). */
export function pauseNinjaCut() {
    try {
        gameInstance?.pause?.();
    } catch { /* ignore */
    }
}

/** Resume a paused game (no-op unless the engine is in the "paused" state). */
export function resumeNinjaCut() {
    try {
        gameInstance?.resume?.();
    } catch { /* ignore */
    }
}
